/**
 * Admin App Config API layer. Typed calls to the SHIPPED STORY-008-004 router
 * (`/admin/config`, `/admin/config/{key}`). Types mirror the backend Pydantic
 * schemas in `apps/api/garden/admin/config_schemas.py`
 * (`AppConfigResponse`, `AppConfigListResponse`, `AppConfigUpsert`). This
 * surface adds NO backend routes.
 */
import { apiClient } from "@/lib/api-client";

/** Frozen `value_type` CHECK mirror (config_schemas.ConfigValueType / ADR-040). */
export const CONFIG_VALUE_TYPES = ["string", "int", "float", "bool", "json"] as const;
export type ConfigValueType = (typeof CONFIG_VALUE_TYPES)[number];

/** The accepted `[minimum, maximum]` range for a bounded key (mirrors
 * `config_schemas.ConfigBoundsInfo`). */
export interface AppConfigBounds {
  minimum: number;
  maximum: number;
}

/** One config row (mirrors `AppConfigResponse`). `value` is always a string.
 *
 * `in_effect` / `bounds` (bug-wave 2026-09-10 fix): a stored value can fall
 * outside a key's registered bounds — e.g. a row written before the bound
 * existed, or written directly to the DB — in which case the reader that
 * owns this key ignores it and falls back to its own default. `in_effect`
 * is `false` in that case so the UI can flag it instead of leaving an admin
 * to believe the stored value is what's actually enforced. Always `true` /
 * `null` for a key with no registered bounds.
 */
export interface AppConfigItem {
  key: string;
  value: string;
  value_type: string;
  description: string | null;
  updated_at: string;
  in_effect: boolean;
  bounds: AppConfigBounds | null;
}

/** List envelope (mirrors `AppConfigListResponse`). */
export interface AppConfigListResponse {
  items: AppConfigItem[];
}

/** PUT body (mirrors `AppConfigUpsert`). */
export interface AppConfigUpsertBody {
  value: string;
  value_type: ConfigValueType;
  description?: string | null;
}

export const configKeys = {
  list: ["admin", "config"] as const,
  detail: (key: string) => ["admin", "config", "detail", key] as const,
} as const;

export function listConfig(): Promise<AppConfigListResponse> {
  return apiClient.get<AppConfigListResponse>("/admin/config");
}

export function getConfig(key: string): Promise<AppConfigItem> {
  return apiClient.get<AppConfigItem>(`/admin/config/${encodeURIComponent(key)}`);
}

export function putConfig(
  key: string,
  body: AppConfigUpsertBody,
): Promise<AppConfigItem> {
  // apiClient.put auto-attaches JWT + Idempotency-Key on the PUT.
  return apiClient.put<AppConfigItem, AppConfigUpsertBody>(
    `/admin/config/${encodeURIComponent(key)}`,
    body,
  );
}
